import * as React from "react"
import { Pencil, X } from "lucide-react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { LoadingSpinner } from "@/components/shared/loading-spinner"
import { MaskedCredentialInput } from "@/components/shared/masked-credential-input"
import { CharacterCounter } from "@/components/shared/character-counter"
import { ArrayHoverList } from "@/components/alfred/array-field-cell"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card"
import { cn } from "@/lib/utils"

export type ConfigPropertyValue =
  | string
  | number
  | boolean
  | string[]
  | unknown[]

export interface EnumOption<T extends string | number = string | number> {
  value: T
  label: string
}

interface ConfigPropertyPresentation {
  label?: string
  section?: string
  fullWidth?: boolean
  /** Current global value shown while this property uses its default. */
  defaultValue?: ConfigPropertyValue
  validate?: (value: ConfigPropertyValue) => string | undefined
  allowDefault?: boolean
  required?: boolean
}

export type ConfigProperty = ConfigPropertyPresentation & (
  | {
      key: string
      type: "string"
      value: string
      isInherited?: boolean
      /**
       * Render as an `<input type="password">` and treat the field as a
       * "rotate-only" secret: empty submits should be omitted from the
       * outgoing payload by the caller.
       */
      password?: boolean
      /** Mask without exposing a password field to browser password managers. */
      maskedCredential?: boolean
      placeholder?: string
      helperText?: string
      characterLimit?: number
      autoFocus?: boolean
    }
  | { key: string; type: "number"; value: number; isInherited?: boolean; min?: number; max?: number; step?: number }
  | { key: string; type: "boolean"; value: boolean; isInherited?: boolean }
  | { key: string; type: "string-array"; value: string[]; isInherited?: boolean }
  | {
      key: string
      type: "json"
      value: unknown[]
      isInherited?: boolean
      itemLabel?: string
      /** Optional label shown for each item when the count is hovered or focused. */
      getItemPreviewLabel?: (item: unknown) => string
      /**
       * Keep an inherited collection on its global default until its custom
       * editor stages a complete replacement.
       */
      replaceInheritedAsCollection?: boolean
      /** Start an inherited collection override from an empty list when Use default is cleared. */
      startEmptyOnDefaultOverride?: boolean
      /**
       * Optional render-prop. When provided, the panel renders the result
       * (typically a "Manage" button that opens a Sheet) next to the count
       * pill. The editor receives the current array, an `onChange` callback
       * to stage edits back into the panel's working state, and a `disabled`
       * flag mirroring the panel's read-only mode. The final argument reports
       * whether the collection currently uses its global default.
      */
      editor?: (
        value: unknown[],
        onChange: (next: unknown[]) => void,
        disabled: boolean,
        accessibleLabel: string,
        usesGlobalDefault: boolean,
      ) => React.ReactNode
    }
  | { key: string; type: "enum"; value: string | number; isInherited?: boolean; options: ReadonlyArray<EnumOption>; placeholder?: string }
  | { key: string; type: "readonly"; value: string; deriveValue?: (values: Readonly<Record<string, ConfigPropertyValue>>) => string }
)

interface ConfigPropertiesPanelProps {
  className?: string
  properties: ConfigProperty[]
  /** Changes only when server-backed data changes (not on incidental parent re-renders). Use a fingerprint of the resolved config payload, not just row metadata like updatedAt. */
  resetKey: unknown
  onSave: (next: Record<string, ConfigPropertyValue | null>) => Promise<void> | void
  /** Open a dedicated editor instead of enabling inline editing. */
  onEdit?: () => void
  isSaving?: boolean
  defaultEditing?: boolean
  showUseDefault?: boolean
  configurationTitle?: string | null
  saveConfirmationTitle: string
  saveConfirmationDescription: string
  editingLocked?: boolean
  submitLabel?: string
  savingLabel?: string
  confirmationActionLabel?: string
  confirmSave?: boolean
  onDirtyChange?: (isDirty: boolean) => void
}

function toFormState(properties: ConfigProperty[]): Record<string, ConfigPropertyValue> {
  return properties.reduce<Record<string, ConfigPropertyValue>>((acc, p) => {
    if (p.type !== "readonly") acc[p.key] = p.value
    return acc
  }, {})
}

function valuesEqual(a: ConfigPropertyValue, b: ConfigPropertyValue): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((v, i) => v === b[i])
  }
  return a === b
}

function displayedValue(
  property: ConfigProperty,
  values: Readonly<Record<string, ConfigPropertyValue>>,
  useDefaults: ReadonlySet<string>,
): ConfigPropertyValue {
  if (useDefaults.has(property.key) && property.defaultValue !== undefined) {
    return property.defaultValue
  }
  return values[property.key]
}

function hasEmptyNumberField(
  properties: ConfigProperty[],
  values: Record<string, ConfigPropertyValue>,
): boolean {
  return properties.some((p) => p.type === "number" && values[p.key] === "")
}

function numberInputValue(value: ConfigPropertyValue): string | number {
  return value === "" ? "" : (value as number)
}

function inheritedKeys(properties: ConfigProperty[]): Set<string> {
  return new Set(properties.filter((p) => "isInherited" in p && p.isInherited).map((p) => p.key))
}

function setsEqual(a: Set<string>, b: Set<string>) {
  return a.size === b.size && [...a].every((k) => b.has(k))
}

interface ConfigPropertySection {
  id: string
  title: string
  properties: ConfigProperty[]
}

function sectionId(title: string) {
  return `configuration-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`
}

function groupPropertiesBySection(properties: ConfigProperty[]): ConfigPropertySection[] {
  const sections = new Map<string, ConfigPropertySection>()

  for (const property of properties) {
    if (!property.section) continue

    let section = sections.get(property.section)
    if (!section) {
      section = {
        id: sectionId(property.section),
        title: property.section,
        properties: [],
      }
      sections.set(property.section, section)
    }
    section.properties.push(property)
  }

  return [...sections.values()]
}

export function ConfigPropertiesPanel({
  className,
  properties,
  resetKey,
  onSave,
  onEdit,
  isSaving = false,
  defaultEditing = false,
  showUseDefault = true,
  configurationTitle = "Device configuration",
  saveConfirmationTitle,
  saveConfirmationDescription,
  editingLocked = false,
  submitLabel = "Save changes",
  savingLabel = "Saving...",
  confirmationActionLabel = "Save changes",
  confirmSave = true,
  onDirtyChange,
}: ConfigPropertiesPanelProps) {
  const [values, setValues] = React.useState<Record<string, ConfigPropertyValue>>(() =>
    toFormState(properties),
  )
  const [original, setOriginal] = React.useState<Record<string, ConfigPropertyValue>>(() =>
    toFormState(properties),
  )
  const [isEditing, setIsEditing] = React.useState(defaultEditing)
  const [useDefaults, setUseDefaults] = React.useState<Set<string>>(() => inheritedKeys(properties))
  const [originalUseDefaults, setOriginalUseDefaults] = React.useState<Set<string>>(() =>
    inheritedKeys(properties),
  )
  const [syncedResetKey, setSyncedResetKey] = React.useState(resetKey)
  const [showSaveConfirmation, setShowSaveConfirmation] = React.useState(false)
  const [showValidationErrors, setShowValidationErrors] = React.useState(false)
  const [touchedKeys, setTouchedKeys] = React.useState<Set<string>>(() => new Set())

  // Reset internal form state when server data changes (e.g. after a successful
  // save or when the selected scope switches), not on every properties[] rebuild.
  if (syncedResetKey !== resetKey) {
    setSyncedResetKey(resetKey)
    const nextValues = toFormState(properties)
    const nextUseDefaults = inheritedKeys(properties)
    setValues(nextValues)
    setOriginal(nextValues)
    setUseDefaults(nextUseDefaults)
    setOriginalUseDefaults(nextUseDefaults)
    setShowValidationErrors(false)
    setTouchedKeys(new Set())
  }

  const hasChanges = React.useMemo(
    () =>
      properties.some((p) => p.type !== "readonly" && !valuesEqual(values[p.key], original[p.key])) ||
      !setsEqual(useDefaults, originalUseDefaults),
    [values, original, properties, useDefaults, originalUseDefaults],
  )

  const hasInvalidNumbers = React.useMemo(
    () => hasEmptyNumberField(properties, values),
    [properties, values],
  )

  React.useEffect(() => {
    onDirtyChange?.(hasChanges)
  }, [hasChanges, onDirtyChange])

  const validationErrors = React.useMemo(() => {
    const next: Record<string, string> = {}
    for (const property of properties) {
      if (property.type === "readonly" || useDefaults.has(property.key) || !property.validate) continue
      const error = property.validate(values[property.key])
      if (error) next[property.key] = error
    }
    return next
  }, [properties, useDefaults, values])

  const handleChange = (key: string, value: ConfigPropertyValue) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const handlePropertyChange = (prop: ConfigProperty, value: ConfigPropertyValue) => {
    if (valuesEqual(value, values[prop.key])) return

    handleChange(prop.key, value)
    if (prop.type !== "json" || !useDefaults.has(prop.key)) return

    setUseDefaults((prev) => {
      const next = new Set(prev)
      next.delete(prop.key)
      return next
    })
  }

  const handlePropertyBlur = (key: string) => {
    setTouchedKeys((previous) => {
      if (previous.has(key)) return previous
      const next = new Set(previous)
      next.add(key)
      return next
    })
  }

  const toggleUseDefault = (prop: ConfigProperty) => {
    const key = prop.key
    const wasUsingDefault = useDefaults.has(key)

    setUseDefaults((prev) => {
      const next = new Set(prev)
      if (wasUsingDefault) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })

    if (
      wasUsingDefault &&
      prop.type === "json" &&
      prop.startEmptyOnDefaultOverride === true &&
      originalUseDefaults.has(key) &&
      valuesEqual(values[key], original[key])
    ) {
      setValues((previous) => ({ ...previous, [key]: [] }))
    }
    setTouchedKeys((previous) => new Set(previous).add(key))
  }

  const handleDiscard = () => {
    setValues(original)
    setUseDefaults(originalUseDefaults)
    setShowValidationErrors(false)
    setTouchedKeys(new Set())
  }

  // Exit edit mode and revert any in-progress edits. Used by both the header
  // X button and as the "always available" cancel affordance.
  const exitEditing = () => {
    setValues(original)
    setUseDefaults(originalUseDefaults)
    setShowValidationErrors(false)
    setTouchedKeys(new Set())
    setIsEditing(false)
  }

  const handleSave = async () => {
    if (!hasChanges || hasInvalidNumbers || Object.keys(validationErrors).length > 0) return
    const payload: Record<string, ConfigPropertyValue | null> = { ...values }
    for (const key of useDefaults) payload[key] = null
    // If onSave rejects, preserve the draft for a retry. The caller owns the
    // user-facing error handling.
    try {
      await onSave(payload)
    } catch {
      // The caller presents the failure; preserve the draft for a retry.
      return
    }
    if (!editingLocked) setIsEditing(false)
  }

  const requestSave = () => {
    setShowValidationErrors(true)
    if (hasInvalidNumbers || Object.keys(validationErrors).length > 0) return
    if (confirmSave) {
      setShowSaveConfirmation(true)
      return
    }
    void handleSave()
  }

  const displayProperties = React.useMemo(() => properties.map((property) => property.type === "readonly" && property.deriveValue
    ? { ...property, value: property.deriveValue(values) }
    : property), [properties, values])
  const sections = React.useMemo(() => groupPropertiesBySection(displayProperties), [displayProperties])
  const hasRequiredProperties = properties.some((property) => property.required)
  const hasCompleteSectionPresentation =
    sections.length > 0 &&
    sections.reduce((count, section) => count + section.properties.length, 0) === properties.length
  const editToggle = editingLocked ? null : isEditing ? (
    <Button
      variant="ghost"
      size="icon"
      onClick={exitEditing}
      disabled={isSaving}
      className="text-muted-foreground hover:text-foreground hover:bg-secondary h-8 w-8"
    >
      <X className="h-4 w-4" />
      <span className="sr-only">Cancel editing</span>
    </Button>
  ) : (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => onEdit ? onEdit() : setIsEditing(true)}
      className="text-muted-foreground hover:text-foreground hover:bg-secondary h-8 w-8"
    >
      <Pencil className="h-4 w-4" />
      <span className="sr-only">Edit properties</span>
    </Button>
  )

  return (
    <div className={cn("space-y-4", hasCompleteSectionPresentation && "mx-auto max-w-[1440px]", className)}>
      {hasCompleteSectionPresentation ? (
        <>
          <div className={cn("flex items-center justify-between gap-4", configurationTitle !== null && "rounded-lg border border-border bg-card px-5 py-4 shadow-sm")}>
            <div>
              {configurationTitle !== null ? (
                <>
                  <p className="text-sm font-semibold text-foreground">{configurationTitle}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {properties.length} properties organized into {sections.length} sections
                  </p>
                </>
              ) : null}
              {hasRequiredProperties ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  Fields marked with <span className="text-destructive">*</span> are required.
                </p>
              ) : null}
            </div>
            {editToggle}
          </div>

          {sections.length > 1 ? (
            <nav
              aria-label="Configuration sections"
              className="flex gap-2 overflow-x-auto pb-1 xl:hidden"
            >
              {sections.map((section) => (
                <Button
                  key={section.id}
                  asChild
                  variant="outline"
                  size="sm"
                  className="h-8 shrink-0 bg-card text-xs text-muted-foreground"
                >
                  <a href={`#${section.id}`}>{section.title}</a>
                </Button>
              ))}
            </nav>
          ) : null}

          <div className={cn("grid items-start gap-6", sections.length > 1 && "xl:grid-cols-[220px_minmax(0,1fr)]")}>
            {sections.length > 1 ? (
              <Card className="sticky top-[132px] hidden max-h-[calc(100vh-156px)] overflow-y-auto border-border bg-card shadow-sm xl:block">
                <CardHeader className="px-4 pb-3 pt-4">
                  <CardTitle className="text-sm">Jump to section</CardTitle>
                  <CardDescription className="text-xs">
                    {sections.length} configuration groups
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-2 pb-3 pt-0">
                  <nav aria-label="Configuration section shortcuts" className="space-y-0.5">
                    {sections.map((section) => (
                      <Button
                        key={section.id}
                        asChild
                        variant="ghost"
                        size="sm"
                        className="h-auto w-full justify-start whitespace-normal px-2 py-1.5 text-left text-xs font-normal text-muted-foreground hover:text-foreground"
                      >
                        <a href={`#${section.id}`}>{section.title}</a>
                      </Button>
                    ))}
                  </nav>
                </CardContent>
              </Card>
            ) : null}

            <div className="min-w-0 space-y-6">
              {sections.map((section) => (
                <Card
                  key={section.id}
                  id={section.id}
                  className="scroll-mt-20 overflow-hidden border-border bg-card shadow-sm md:scroll-mt-[132px]"
                >
                  <CardHeader className="border-b border-border bg-muted/40 px-5 py-4">
                    <CardTitle className="text-base">
                      <h2>{section.title}</h2>
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {section.properties.length} {section.properties.length === 1 ? "property" : "properties"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-x-8 gap-y-1 p-3 md:grid-cols-2">
                    {section.properties.map((prop) => (
                      <PropertyTile
                        key={prop.key}
                        prop={prop}
                        value={displayedValue(prop, values, useDefaults)}
                        isEditing={isEditing && !isSaving}
                        useDefault={useDefaults.has(prop.key)}
                        showUseDefault={showUseDefault}
                        onChange={(value) => handlePropertyChange(prop, value)}
                        onBlur={() => handlePropertyBlur(prop.key)}
                        onToggleUseDefault={() => toggleUseDefault(prop)}
                        validationError={showValidationErrors || touchedKeys.has(prop.key) ? validationErrors[prop.key] : undefined}
                      />
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            <div className="table-scroll-x overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow className="border-border hover:bg-card">
                    <TableHead className="text-muted-foreground font-medium w-1/2">Property</TableHead>
                    <TableHead className="text-muted-foreground font-medium">Current value</TableHead>
                    <TableHead className="w-12 text-right pr-3">{editToggle}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayProperties.map((prop, index) => {
                    const label = prop.label ?? prop.key
                    const useDefaultId = `use-default-${prop.key}`

                    return (
                      <TableRow
                        key={prop.key}
                        data-field-key={prop.key}
                        className={cn(
                          "border-border transition-colors",
                          index % 2 === 1 && "bg-muted/30",
                        )}
                      >
                        <TableCell className="align-middle py-3">
                          <PropertyLabel label={label} required={prop.required} />
                        </TableCell>
                        <TableCell className="py-3">
                          <div data-field-controls className="flex min-w-0 flex-col items-start gap-2.5">
                            <PropertyControl
                              prop={prop}
                              value={displayedValue(prop, values, useDefaults)}
                              useDefault={useDefaults.has(prop.key)}
                              disabled={
                                !isEditing || isSaving ||
                                (prop.type !== "json" && useDefaults.has(prop.key))
                              }
                              onChange={(value) => handlePropertyChange(prop, value)}
                              onBlur={() => handlePropertyBlur(prop.key)}
                              validationError={showValidationErrors || touchedKeys.has(prop.key) ? validationErrors[prop.key] : undefined}
                            />
                            {showUseDefault ? (
                              <UseDefaultControl
                                prop={prop}
                                label={label}
                                id={useDefaultId}
                                checked={useDefaults.has(prop.key)}
                                disabled={
                                  !isEditing || isSaving ||
                                  (prop.type === "json" &&
                                    prop.replaceInheritedAsCollection === true &&
                                    prop.startEmptyOnDefaultOverride !== true &&
                                    prop.isInherited === true &&
                                    useDefaults.has(prop.key))
                                }
                                onToggle={() => toggleUseDefault(prop)}
                              />
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="w-12" />
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
      {isEditing && (
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={handleDiscard}
            disabled={!hasChanges || isSaving}
            className="bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            Discard changes
          </Button>
          <Button
            onClick={requestSave}
            disabled={
              !hasChanges ||
              hasInvalidNumbers ||
              Object.keys(validationErrors).length > 0 ||
              isSaving
            }
            className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
          >
            {isSaving && <LoadingSpinner size="sm" className="mr-2" />}
            {isSaving ? savingLabel : submitLabel}
          </Button>
        </div>
      )}
      <AlertDialog open={confirmSave && showSaveConfirmation} onOpenChange={setShowSaveConfirmation}>
        <AlertDialogContent className="max-w-[400px] border-border bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              {saveConfirmationTitle}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              {saveConfirmationDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSave}>{confirmationActionLabel}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface PropertyTileProps {
  prop: ConfigProperty
  value: ConfigPropertyValue
  isEditing: boolean
  useDefault: boolean
  showUseDefault: boolean
  onChange: (next: ConfigPropertyValue) => void
  onBlur: () => void
  onToggleUseDefault: () => void
  validationError?: string
}

function PropertyTile({
  prop,
  value,
  isEditing,
  useDefault,
  showUseDefault,
  onChange,
  onBlur,
  onToggleUseDefault,
  validationError,
}: PropertyTileProps) {
  const label = prop.label ?? prop.key
  const useDefaultId = `use-default-${prop.key}`

  return (
    <div
      data-field-key={prop.key}
      className={cn(
        "min-w-0 rounded-md px-3 py-4 transition-colors hover:bg-muted/30",
        prop.fullWidth && isEditing && useDefault && "md:col-span-2",
      )}
    >
      <div data-field-controls className="flex min-w-0 flex-col items-start gap-2.5">
        <PropertyLabel label={label} required={prop.required} />
        <PropertyControl
          prop={prop}
          value={value}
          useDefault={useDefault}
          disabled={!isEditing || (prop.type !== "json" && useDefault)}
          onChange={onChange}
          onBlur={onBlur}
          validationError={validationError}
        />
        {showUseDefault ? (
          <UseDefaultControl
            prop={prop}
            label={label}
            id={useDefaultId}
            checked={useDefault}
            disabled={
              !isEditing ||
              (prop.type === "json" &&
                prop.replaceInheritedAsCollection === true &&
                prop.startEmptyOnDefaultOverride !== true &&
                prop.isInherited === true &&
                useDefault)
            }
            onToggle={onToggleUseDefault}
          />
        ) : null}
      </div>
    </div>
  )
}

interface PropertyControlProps {
  prop: ConfigProperty
  value: ConfigPropertyValue
  useDefault: boolean
  disabled: boolean
  onChange: (next: ConfigPropertyValue) => void
  onBlur: () => void
  validationError?: string
}

function PropertyControl({ prop, value, useDefault, disabled, onChange, onBlur, validationError }: PropertyControlProps) {
  if (prop.type === "readonly") {
    return (
      <div className="flex min-h-9 items-center break-all text-sm font-mono text-foreground">
        {prop.value}
      </div>
    )
  }

  return (
    <PropertyValueInput
      prop={prop}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      useDefault={useDefault}
      disabled={disabled}
      validationError={validationError}
    />
  )
}

interface UseDefaultControlProps {
  prop: ConfigProperty
  label: string
  id: string
  checked: boolean
  disabled: boolean
  onToggle: () => void
}

function UseDefaultControl({
  prop,
  label,
  id,
  checked,
  disabled,
  onToggle,
}: UseDefaultControlProps) {
  if (prop.type === "readonly" || prop.allowDefault === false) return null

  return (
    <div className="flex shrink-0 items-center gap-2">
      <Checkbox
        id={id}
        aria-label={`Use default for ${label}`}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onToggle}
        className="border-input data-[state=checked]:bg-primary data-[state=checked]:border-primary"
      />
      <label
        htmlFor={id}
        className="cursor-pointer select-none whitespace-nowrap text-xs text-muted-foreground"
      >
        Use default
      </label>
    </div>
  )
}

interface PropertyValueInputProps {
  prop: ConfigProperty
  value: ConfigPropertyValue
  onChange: (next: ConfigPropertyValue) => void
  onBlur: () => void
  useDefault: boolean
  disabled?: boolean
  validationError?: string
}

function PropertyValueInput({
  prop,
  value,
  onChange,
  onBlur,
  useDefault,
  disabled = false,
  validationError,
}: PropertyValueInputProps) {
  const label = prop.label ?? prop.key

  switch (prop.type) {
    case "boolean":
      return (
        <div className="flex items-center gap-3">
          <Switch
            aria-label={label}
            checked={value as boolean}
            disabled={disabled}
            onCheckedChange={(checked) => onChange(checked)}
            className="data-[state=checked]:bg-primary disabled:cursor-not-allowed disabled:opacity-60"
          />
          <span className="text-sm text-muted-foreground">
            {(value as boolean) ? "true" : "false"}
          </span>
        </div>
      )
    case "number": {
      const isEmpty = value === ""
      return (
        <Input
          aria-label={label}
          aria-required={prop.required || undefined}
          type="number"
          min={prop.min}
          max={prop.max}
          step={prop.step ?? 1}
          value={numberInputValue(value)}
          disabled={disabled}
          aria-invalid={!disabled && isEmpty}
          onChange={(e) => {
            const raw = e.target.value
            if (raw === "") {
              onChange("")
              return
            }
            const parsed = Number(raw)
            onChange(Number.isNaN(parsed) ? "" : parsed)
          }}
          className="bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] max-w-xs aria-invalid:border-destructive"
          onBlur={onBlur}
        />
      )
    }
    case "string-array": {
      const arr = (value as string[]) ?? []
      return (
        <Input
          aria-label={label}
          aria-required={prop.required || undefined}
          type="text"
          value={arr.join(", ")}
          disabled={disabled}
          onChange={(e) => {
            const next = e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
            onChange(next)
          }}
          placeholder="Comma separated"
          onBlur={onBlur}
          className="bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] max-w-md"
        />
      )
    }
    case "json": {
      const arr = (value as unknown[]) ?? []
      const itemLabel = prop.itemLabel ?? "item"
      const count = arr.length
      const countPill = (
        <span
          className="inline-flex items-center rounded-md border border-input bg-muted/40 px-2 py-1 text-xs text-muted-foreground cursor-default"
          tabIndex={prop.getItemPreviewLabel ? 0 : undefined}
        >
          {count} {count === 1 ? itemLabel : `${itemLabel}s`}
        </span>
      )
      return (
        <div className="flex items-center gap-3">
          {prop.getItemPreviewLabel ? (
            <HoverCard openDelay={200} closeDelay={100}>
              <HoverCardTrigger asChild>{countPill}</HoverCardTrigger>
              <HoverCardContent className="w-72 bg-popover border-border" align="start">
                <ArrayHoverList items={arr.map(prop.getItemPreviewLabel)} />
              </HoverCardContent>
            </HoverCard>
          ) : countPill}
          {prop.editor ? (
            prop.editor(arr, (next) => onChange(next), disabled, label, useDefault)
          ) : (
            <span className="text-xs text-muted-foreground italic">
              advanced editor coming soon
            </span>
          )}
        </div>
      )
    }
    case "enum": {
      // Radix Select coerces values to strings, so we round-trip through
      // String() and look the original typed value back up via the options.
      const stringValue = String(value)
      return (
        <Select
          value={stringValue}
          disabled={disabled}
          onValueChange={(next) => {
            const match = prop.options.find((o) => String(o.value) === next)
            if (match) onChange(match.value)
          }}
        >
          <SelectTrigger
            aria-label={label}
            aria-required={prop.required || undefined}
            aria-invalid={validationError ? true : undefined}
            onBlur={onBlur}
            className="bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px] max-w-xs aria-invalid:border-destructive"
          >
            <SelectValue placeholder={prop.placeholder} />
          </SelectTrigger>
          <SelectContent>
            {prop.options.map((opt) => (
              <SelectItem key={String(opt.value)} value={String(opt.value)}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }
    case "string":
    default: {
      const isPassword = prop.type === "string" && prop.password === true
      const isMaskedCredential =
        prop.type === "string" && prop.maskedCredential === true
      const placeholder = prop.type === "string" ? prop.placeholder : undefined
      const helperText = prop.type === "string" ? prop.helperText : undefined
      return (
        <div className="space-y-1.5 max-w-xs">
          {isMaskedCredential ? (
            <MaskedCredentialInput
              aria-label={label}
              aria-required={prop.required || undefined}
              value={value as string}
              disabled={disabled}
              aria-invalid={validationError ? true : undefined}
              autoFocus={prop.type === "string" ? prop.autoFocus : undefined}
              placeholder={placeholder}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              className="bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px]"
            />
          ) : (
            <Input
              aria-label={label}
              aria-required={prop.required || undefined}
              type={isPassword ? "password" : "text"}
              value={value as string}
              disabled={disabled}
              aria-invalid={validationError ? true : undefined}
              autoFocus={prop.type === "string" ? prop.autoFocus : undefined}
              placeholder={placeholder}
              autoComplete={isPassword ? "new-password" : undefined}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              className="bg-transparent border-input text-foreground placeholder:text-muted-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px]"
            />
          )}
          {validationError ? (
            <p className="text-xs text-destructive">{validationError}</p>
          ) : helperText ? (
            <p className="text-xs text-muted-foreground">{helperText}</p>
          ) : null}
          {prop.type === "string" && prop.characterLimit ? (
            <CharacterCounter value={value as string} maxLength={prop.characterLimit} />
          ) : null}
        </div>
      )
    }
  }
}

function PropertyLabel({ label, required }: { label: string; required?: boolean }) {
  return (
    <span className="text-sm font-medium text-foreground">
      {label}
      {required ? (
        <>
          <span aria-hidden="true" className="ml-1 text-destructive">*</span>
          <span className="sr-only"> (required)</span>
        </>
      ) : null}
    </span>
  )
}
